import { appendLog } from './logStore.js';
import { fetchControl } from './api/services/controlService.js';

const CONTROL_KEY = 'didapor:control:v1';

export function readCachedControl() {
  try {
    return JSON.parse(localStorage.getItem(CONTROL_KEY) || 'null');
  } catch {
    return null;
  }
}

export function saveCachedControl(c) {
  try {
    localStorage.setItem(CONTROL_KEY, JSON.stringify(c));
  } catch {}
}

/** Ambil control dari server via API service (repository). */
export async function fetchLocalControl() {
  return fetchControl();
}

export function normalizeControl(raw) {
  const nowIso = new Date().toISOString();
  const out = {
    version: 1,
    updatedAt: raw?.updatedAt || nowIso,
    applyOnBoot: raw?.applyOnBoot !== false,
    power: { state: raw?.power?.state || 'on' },
    audio: {
      volume: clamp01(raw?.audio?.volume, 0, 100, 60),
      muted: !!raw?.audio?.muted,
      soundOutput: raw?.audio?.soundOutput ? String(raw.audio.soundOutput) : 'speaker',
    },
    display: {
      brightness: clamp01(raw?.display?.brightness, 0, 100, 70),
      rotation: normalizeRotation(raw?.display?.rotation),
    },
    update: normalizeUpdate(raw?.update),
    device: normalizeDevice(raw?.device),
  };
  return out;
}

function clamp01(n, min, max, fallback) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, v));
}

function normalizeRotation(v) {
  const n = Number(v);
  const cand = [0, 90, 180, 270];
  return cand.includes(n) ? n : 0;
}

function normalizeUpdate(u) {
  try {
    if (!u) return { manifestUrls: [] };
    const list = [];
    if (Array.isArray(u.manifestUrls)) {
      for (const x of u.manifestUrls) if (x) list.push(String(x));
    }
    if (u.url) list.unshift(String(u.url));
    return { manifestUrls: list };
  } catch {
    return { manifestUrls: [] };
  }
}

function normalizeDevice(d) {
  try {
    const reportUrl = d?.reportUrl ? String(d.reportUrl) : '';
    const includeMac = d?.includeMac !== false;
    return { reportUrl, includeMac };
  } catch {
    return { reportUrl: '', includeMac: true };
  }
}

export function applyCssDisplay(control) {
  try {
    const rot = control?.display?.rotation ?? 0;
    const brightPct = control?.display?.brightness ?? 100;
    const root = document.documentElement;
    root.style.setProperty('--ctl-rotation', `${rot}deg`);
    const b = Math.max(0, Math.min(200, brightPct)) / 100; // CSS brightness 0..2
    root.style.setProperty('--ctl-brightness', String(b));
    // For devices where video is on hardware overlay (CSS filter not applied), provide optional dim overlay.
    const dim = Math.max(0, Math.min(1, (100 - Math.max(0, Math.min(100, brightPct))) / 100));
    root.style.setProperty('--ctl-dim', String(dim));
  } catch {}
}

export function deriveMediaAudio(control) {
  const vol = Math.max(0, Math.min(100, Number(control?.audio?.volume ?? 100)));
  const muted = !!control?.audio?.muted;
  return {
    startWithSound: !muted && vol > 0,
    initialVolume: Math.max(0, Math.min(1, vol / 100)),
  };
}

function applySystemAudio(svc, control) {
  const vol = Number(control?.audio?.volume);
  const muted = !!control?.audio?.muted;
  // Try com.webos.audio first
  try {
    svc.request('luna://com.webos.audio', {
      method: 'setMuted',
      parameters: { muted, soundOutput: control?.audio?.soundOutput },
      subscribe: false,
      onSuccess: (r) => {
        try {
          appendLog({ type: 'info', detail: { event: 'audio-setMuted-ok', r } });
        } catch {}
      },
      onFailure: (e) => {
        try {
          appendLog({ type: 'warn', detail: { event: 'audio-setMuted-fail', e } });
        } catch {}
      },
    });
  } catch (e) {
    try {
      appendLog({
        type: 'warn',
        detail: { event: 'audio-setMuted-ex', e: String(e?.message || e) },
      });
    } catch {}
  }
  if (Number.isFinite(vol)) {
    try {
      svc.request('luna://com.webos.audio', {
        method: 'setVolume',
        parameters: {
          volume: Math.round(Math.max(0, Math.min(100, vol))),
          soundOutput: control?.audio?.soundOutput || 'speaker',
        },
        subscribe: false,
        onSuccess: (r) => {
          try {
            appendLog({ type: 'info', detail: { event: 'audio-setVolume-ok', r } });
          } catch {}
        },
        onFailure: (e) => {
          try {
            appendLog({ type: 'warn', detail: { event: 'audio-setVolume-fail', e } });
          } catch {}
        },
      });
    } catch (e) {
      try {
        appendLog({
          type: 'warn',
          detail: { event: 'audio-setVolume-ex', e: String(e?.message || e) },
        });
      } catch {}
    }
  }
  // Also try settingsservice sound category (some models honor this)
  const soundSettings = {};
  if (Number.isFinite(vol)) soundSettings.volume = Math.round(Math.max(0, Math.min(100, vol)));
  soundSettings.muted = muted;
  // Try alternative keys known across models
  if (Number.isFinite(vol)) {
    soundSettings.masterVolumeLevel = soundSettings.volume;
    soundSettings.masterVolume = soundSettings.volume;
  }
  try {
    svc.request('luna://com.webos.settingsservice', {
      method: 'setSystemSettings',
      parameters: { category: 'sound', settings: soundSettings },
      subscribe: false,
      onSuccess: (r) => {
        try {
          appendLog({ type: 'info', detail: { event: 'settings-sound-ok', r } });
        } catch {}
      },
      onFailure: (e) => {
        try {
          appendLog({ type: 'warn', detail: { event: 'settings-sound-fail', e } });
        } catch {}
      },
    });
  } catch (e) {
    try {
      appendLog({
        type: 'warn',
        detail: { event: 'settings-sound-ex', e: String(e?.message || e) },
      });
    } catch {}
  }
}

function applySystemBrightness(svc, control) {
  const bright = Number(control?.display?.brightness);
  if (!Number.isFinite(bright)) return;
  try {
    svc.request('luna://com.webos.settingsservice', {
      method: 'setSystemSettings',
      parameters: {
        category: 'picture',
        settings: {
          brightness: Math.round(Math.max(0, Math.min(100, bright))),
          backlight: Math.round(Math.max(0, Math.min(100, bright))),
          oledLight: Math.round(Math.max(0, Math.min(100, bright))),
          panelBrightness: Math.round(Math.max(0, Math.min(100, bright))),
        },
      },
      subscribe: false,
      onSuccess: (r) => {
        try {
          appendLog({ type: 'info', detail: { event: 'settings-picture-ok', r } });
        } catch {}
      },
      onFailure: (e) => {
        try {
          appendLog({ type: 'warn', detail: { event: 'settings-picture-fail', e } });
        } catch {}
      },
    });
  } catch (e) {
    try {
      appendLog({
        type: 'warn',
        detail: { event: 'settings-picture-ex', e: String(e?.message || e) },
      });
    } catch {}
  }
}

function applySystemScreenPower(svc, control) {
  const p = String(control?.power?.state || 'on').toLowerCase();
  if (p === 'off') {
    try {
      svc.request('luna://com.webos.service.tvpower', {
        method: 'turnOffScreen',
        parameters: {},
        subscribe: false,
      });
    } catch {}
    try {
      svc.request('luna://com.webos.service.tvpower', {
        method: 'screenOff',
        parameters: {},
        subscribe: false,
      });
    } catch {}
    try {
      svc.request('luna://com.webos.service.display', {
        method: 'setState',
        parameters: { state: 'off' },
        subscribe: false,
      });
    } catch {}
  } else if (p === 'on') {
    try {
      svc.request('luna://com.webos.service.tvpower', {
        method: 'turnOnScreen',
        parameters: {},
        subscribe: false,
      });
    } catch {}
    try {
      svc.request('luna://com.webos.service.tvpower', {
        method: 'screenOn',
        parameters: {},
        subscribe: false,
      });
    } catch {}
    try {
      svc.request('luna://com.webos.service.display', {
        method: 'setState',
        parameters: { state: 'on' },
        subscribe: false,
      });
    } catch {}
    try {
      svc.request('luna://com.webos.service.tvpower', {
        method: 'wakeUp',
        parameters: {},
        subscribe: false,
      });
    } catch {}
  }
}

export function applySystemIfAvailable(control) {
  const svc = window?.webOS?.service;
  if (!svc) return;
  try {
    applySystemAudio(svc, control);
    applySystemBrightness(svc, control);
    applySystemScreenPower(svc, control);
  } catch {}
}

export function isScreenOff(control) {
  const p = String(control?.power?.state || 'on').toLowerCase();
  return p === 'off';
}
